import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { errorToast, successToast } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";

import {
  axiosPostData,
  axiosPatchData,
  axiosDeleteData,
} from "@shared/api/clients";

import {
  AdminUserDetailResponse,
  CreateAdminUserRequest,
  CreateAdminUserResponse,
  UpdateAdminUserRequest,
} from "./adminUsers.types";

const ADMIN_USERS_ENDPOINT = "v1/admin/users";
const ADMIN_USERS_QUERY_KEY = ["admin", "users"];

export const createAdminUser = async (
  body: CreateAdminUserRequest,
): Promise<CreateAdminUserResponse> => {
  return axiosPostData<CreateAdminUserResponse, CreateAdminUserRequest>(
    ADMIN_USERS_ENDPOINT,
    body,
  );
};

export const updateAdminUser = async ({
  id,
  ...body
}: UpdateAdminUserRequest & { id: string }): Promise<AdminUserDetailResponse> => {
  return axiosPatchData<AdminUserDetailResponse, UpdateAdminUserRequest>(
    `${ADMIN_USERS_ENDPOINT}/${id}`,
    body,
  );
};

export const deleteAdminUser = async (id: string): Promise<void> => {
  await axiosDeleteData<void>(`${ADMIN_USERS_ENDPOINT}/${id}`);
};

export const useCreateAdminUserMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: createAdminUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_USERS_QUERY_KEY,
        exact: false,
      });
      successToast({
        description: translate("common.createSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorCreating"),
      });
    },
  });
};

export const useUpdateAdminUserMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: updateAdminUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_USERS_QUERY_KEY,
        exact: false,
      });
      successToast({
        description: translate("common.editSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};

export const useDeleteAdminUserMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: deleteAdminUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_USERS_QUERY_KEY,
        exact: false,
      });
      successToast({
        description: translate("common.deleteSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorDeleting"),
      });
    },
  });
};
